import { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Avatar,
  Chip,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import HomeIcon from '@mui/icons-material/Home';
import PersonIcon from '@mui/icons-material/Person';
import { AdminBusinessDetail } from '../../../types/admin';
import { formatDistanceToNow } from 'date-fns';

interface BusinessClientsTabProps {
  business: AdminBusinessDetail;
}

/**
 * BusinessClientsTab - Display all clients across all agents
 *
 * Features:
 * - Client list with avatars
 * - Agent assignment display
 * - Status (active/inactive)
 * - Homes count
 * - Search by name or email
 * - Filter by agent
 * - Filter by status
 * - Pagination
 */
export default function BusinessClientsTab({ business }: BusinessClientsTabProps) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAgent, setSelectedAgent] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Filter clients
  const filteredClients = business.clients.filter((client) => {
    const matchesSearch =
      searchQuery === '' ||
      client.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      client.email.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesAgent = selectedAgent === 'all' || client.agent_email === selectedAgent;

    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' && client.status === 'active') ||
      (statusFilter === 'inactive' && client.status === 'inactive');

    return matchesSearch && matchesAgent && matchesStatus;
  });

  // Paginate clients
  const paginatedClients = filteredClients.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: { target: { value: string } }) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get unique agents for filter
  const uniqueAgents = Array.from(
    new Set(business.clients.map((c) => c.agent_email))
  ).map((email) => {
    const agent = business.clients.find((c) => c.agent_email === email);
    return {
      email,
      name: agent?.agent_name || '',
    };
  });

  return (
    <Box sx={{ p: 3 }}>
      {/* Summary Stats */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Client Summary
        </Typography>
        <Box sx={{ display: 'flex', gap: 4, mt: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Total Clients
            </Typography>
            <Typography variant="h5" fontWeight={600}>
              {business.analytics.clients.total}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Active Clients
            </Typography>
            <Typography variant="h5" fontWeight={600} color="success.main">
              {business.analytics.clients.active}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              New This Month
            </Typography>
            <Typography variant="h5" fontWeight={600} color="primary.main">
              {business.analytics.clients.new_this_month}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Churn Rate
            </Typography>
            <Typography variant="h5" fontWeight={600} color="error.main">
              {business.analytics.clients.churn_rate.toFixed(1)}%
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Filters */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search clients..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(0);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1, minWidth: 250 }}
        />

        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Agent</InputLabel>
          <Select
            value={selectedAgent}
            onChange={(e) => {
              setSelectedAgent(e.target.value);
              setPage(0);
            }}
            label="Filter by Agent"
          >
            <MenuItem value="all">All Agents</MenuItem>
            {uniqueAgents.map((agent) => (
              <MenuItem key={agent.email} value={agent.email}>
                {agent.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
            label="Status"
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Clients Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Client</TableCell>
              <TableCell>Agent</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Homes</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Last Activity</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedClients.map((client) => (
              <TableRow key={client.id} hover>
                {/* Client Info */}
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar src={client.picture} alt={client.name} sx={{ width: 40, height: 40 }} />
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {client.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        component="a"
                        href={`mailto:${client.email}`}
                        sx={{
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        {client.email}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>

                {/* Agent */}
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PersonIcon fontSize="small" color="action" />
                    <Box>
                      <Typography variant="body2">{client.agent_name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {client.agent_email}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>

                {/* Status */}
                <TableCell>
                  <Chip
                    label={client.status === 'active' ? 'Active' : 'Inactive'}
                    size="small"
                    color={client.status === 'active' ? 'success' : 'default'}
                  />
                </TableCell>

                {/* Homes Count */}
                <TableCell align="center">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                    <HomeIcon fontSize="small" color="action" />
                    <Typography variant="body2" fontWeight={500}>
                      {client.homes_count}
                    </Typography>
                  </Box>
                </TableCell>

                {/* Created */}
                <TableCell>
                  <Tooltip title={new Date(client.created_at).toLocaleString()}>
                    <Typography variant="body2" color="text.secondary">
                      {formatDistanceToNow(new Date(client.created_at), { addSuffix: true })}
                    </Typography>
                  </Tooltip>
                </TableCell>

                {/* Last Activity */}
                <TableCell>
                  <Tooltip title={new Date(client.last_activity).toLocaleString()}>
                    <Typography variant="body2" color="text.secondary">
                      {formatDistanceToNow(new Date(client.last_activity), { addSuffix: true })}
                    </Typography>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {filteredClients.length === 0 && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No clients found matching your filters
            </Typography>
          </Box>
        )}

        <TablePagination
          component="div"
          count={filteredClients.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>
    </Box>
  );
}
